import { Box, Button, Chip, Collapse } from '@mui/material';
import { alpha } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import type { UnplacedCalendarWork } from '@/features/calendar/calendarSegments';
import { approvalWaitLabel, showsApprovalWaitMarker } from '@/utils/workOrderStatusChips';

function itemLabel(item: UnplacedCalendarWork): string {
  return [item.workOrderNumber, item.vehicleRegNumber, item.title]
    .filter((part) => part != null && part !== '')
    .join(' · ');
}

/** One list id per page: the toggle and the card are rendered apart (#4182). */
const UNPLACED_LIST_ID = 'calendar-unplaced-work-list';

/**
 * Opens the needs-placement card. It sits in the legend row beside the
 * hide-absent switch, so a collapsed card takes no row of its own (#4182).
 */
export function UnplacedWorkToggle({
  count,
  expanded,
  onToggle,
}: {
  count: number;
  expanded: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation('calendar');
  if (count === 0) return null;
  return (
    <Button
      size="small"
      color="warning"
      variant={expanded ? 'contained' : 'outlined'}
      onClick={onToggle}
      aria-expanded={expanded}
      aria-controls={UNPLACED_LIST_ID}
      data-testid="unplaced-work-toggle"
      endIcon={
        <ExpandMoreIcon
          sx={{
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: (theme) => theme.transitions.create('transform'),
          }}
        />
      }
      sx={{ whiteSpace: 'nowrap' }}
    >
      {t('unplaced.title', { count })}
    </Button>
  );
}

/**
 * Compact needs-placement list for floating work with no mechanic queue
 * placement (#2686). Timed calendar rows never synthesize a slot for these.
 * Rendered only while opened from {@link UnplacedWorkToggle}, so a long list
 * never pushes the grid down unasked (#3319, #4182).
 */
export function UnplacedWorkBucket({
  items,
  expanded,
  onSelect,
}: {
  items: UnplacedCalendarWork[];
  expanded: boolean;
  onSelect: (bookingId: string) => void;
}) {
  const { t } = useTranslation('calendar');
  if (items.length === 0) return null;

  return (
    <Collapse in={expanded} unmountOnExit>
      <Box
        data-testid="unplaced-work-bucket"
        sx={{
          mb: 1,
          px: 1.5,
          py: 0.75,
          borderRadius: 1,
          border: 1,
          borderColor: 'warning.light',
          bgcolor: (theme) => alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.12 : 0.08),
        }}
      >
        <Box
          id={UNPLACED_LIST_ID}
          sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75 }}
        >
          {items.map((item) => {
            const label = itemLabel(item);
            // Derived approval wait (#3027): an icon on the chip, named in its aria label.
            const approvalText = showsApprovalWaitMarker({
              status: item.bookingStatus,
              isWaitingForApproval: item.isWaitingForApproval,
            })
              ? approvalWaitLabel(t('statusLabels.waiting_approval'), item.openApprovalCount)
              : null;
            const openLabel = t('unplaced.openWorkOrder', {
              number: item.workOrderNumber || item.vehicleRegNumber || label,
            });
            return (
              <Chip
                key={item.bookingId}
                size="small"
                variant="outlined"
                label={label}
                component={RouterLink}
                to={`/workorders/${item.bookingId}`}
                clickable
                onClick={(event: React.MouseEvent) => {
                  // Modified and middle clicks stay with the link so "Open in new tab" works (#4108).
                  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
                  event.preventDefault();
                  onSelect(item.bookingId);
                }}
                {...(approvalText
                  ? { icon: <PendingActionsIcon titleAccess={approvalText} data-testid="approval-wait-indicator" /> }
                  : {})}
                aria-label={approvalText ? `${openLabel} — ${approvalText}` : openLabel}
                data-testid={`unplaced-work-${item.bookingId}`}
              />
            );
          })}
        </Box>
      </Box>
    </Collapse>
  );
}
